import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Config from '../Config';

const useAddress = () => {
  const user = useSelector(state => state.UserReducer.user);
  const token = user?.token;
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchUserAddresses = useCallback(async () => {

    if (!token) return [];

    try {
      const addressResponse = await axios.get(`${Config.apiUrl}/addresses`, {
        headers: { Authorization: `Bearer ${token}` } 
      });

      const responseData = addressResponse.data.data || addressResponse.data || {};
      const userAddresses = responseData.addresses || responseData || [];

      setAddresses(userAddresses);

      if (userAddresses.length > 0) {
        const defaultAddr = userAddresses.find(a => a.is_default) || userAddresses[0];
        if (defaultAddr) {
          setSelectedAddressId(defaultAddr.id);
        }
      }
      return userAddresses;
    } catch (addressError) {
      // console.error('Fetch addresses error:', addressError);
      setAddresses([]);
      return [];
    }
  }, [token]);

  const deleteAddress = async (addressId) => {
    try {
      setLoading(true);
      setError('');

      await axios.delete(`${Config.apiUrl}/addresses/${addressId}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });

      const updatedAddresses = addresses.filter(addr => addr.id !== addressId);
      setAddresses(updatedAddresses);

      if (selectedAddressId === addressId) {
        if (updatedAddresses.length > 0) {
          const defaultAddr = updatedAddresses.find(a => a.is_default) || updatedAddresses[0];
          setSelectedAddressId(defaultAddr.id);
        } else {
          setSelectedAddressId(null);
        }
      }
      setSuccess('Address deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      // console.error('Delete address error:', err);
      setError('Failed to delete address');
    } finally {
      setLoading(false);
    }
  };

  const updateAddress = async (addressId, addressData) => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.put(`${Config.apiUrl}/addresses/${addressId}`, addressData, {
        headers: { Authorization: `Bearer ${user.token}` }
      });

      const updatedAddress = response.data.data?.address || response.data.data || response.data;

      const updatedAddresses = addresses.map(addr =>
        addr.id === addressId ? { ...addr, ...updatedAddress } : addr
      );
      setAddresses(updatedAddresses);

      setSuccess('Address updated successfully!');
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      // console.error('Update address error:', err);
      setError('Failed to update address');
    } finally {
      setLoading(false);
    }
  };

  const createNewAddress = async (addressData) => {
    try {
      setLoading(true);
      setError('');

      const payload = { ...addressData };
      // if (!payload.first_name) payload.first_name = user.first_name;
      // if (!payload.last_name) payload.last_name = user.last_name;
      // if (!payload.phone) payload.phone = user.phone;

      const response = await axios.post(`${Config.apiUrl}/addresses`, payload, {
        headers: { Authorization: `Bearer ${user.token}` }
      });

      const newAddress = response.data.data?.address || response.data.data || response.data;

      const updatedAddresses = [...addresses, newAddress];
      setAddresses(updatedAddresses);

      if (addresses.length === 0 || newAddress.is_default) {
        setSelectedAddressId(newAddress.id);
      }

      setSuccess('Address added successfully!');
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      // console.error('Add address error:', err);
      // setError(err.response?.data?.message || 'Failed to add the address');
      setError(err.response?.data?.errors && err.response.data.errors.length > 0 ? err.response.data.errors[0].message : 'Failed to add the address');
    } finally {
      setLoading(false);
    }
  };

  const setAsDefaultAddress = async (addressId) => {
    try {
      setLoading(true);
      setError('');

      await axios.put(`${Config.apiUrl}/addresses/${addressId}/set-default`, {}, {
        headers: { Authorization: `Bearer ${user.token}` }
      });

      const updatedAddresses = addresses.map(addr => ({
        ...addr,
        is_default: addr.id === addressId
      }));
      setAddresses(updatedAddresses);

      setSelectedAddressId(addressId);
      setSuccess('Default address updated successfully!');
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      // console.error('Set default address error:', err);
      setError(err.response?.data?.errors && err.response.data.errors.length > 0 ? err.response.data.errors[0].message : 'Failed to set default address');
    } finally {
      setLoading(false);
    }
  };

  return {
    addresses,
    setAddresses,
    selectedAddressId,
    setSelectedAddressId,
    loading,
    error,
    setError,
    success,
    setSuccess,
    fetchUserAddresses,
    deleteAddress,
    updateAddress,
    createNewAddress,
    setAsDefaultAddress
  };
};

export default useAddress;
